import { getUTMCookie } from "@/lib/authCookieServer";

/** Cookie dimensions written by {@link UtmChannelCapture} (matches query: utm_source → source, …). */
export const UTM_CHANNEL_KEYS = [
  "source",
  "medium",
  "campaign",
  "content",
  "term",
  "id",
  "fbclid",
  "gclid",
] as const;

/** Optional lead API click identifiers (from query params, last-touch cookies). */
export const LEAD_CLICK_ID_KEYS = ["fbclid", "gclid"] as const;
export type LeadClickIdKey = (typeof LEAD_CLICK_ID_KEYS)[number];

export type UtmChannelKey = (typeof UTM_CHANNEL_KEYS)[number];

/** One snapshot: source, medium, campaign, content, term, id (empty string if unset). */
export type UtmChannelSnapshot = Record<UtmChannelKey, string>;

function emptySnapshot(): UtmChannelSnapshot {
  return {
    source: "",
    medium: "",
    campaign: "",
    content: "",
    term: "",
    id: "",
    fbclid: "",
    gclid: "",
  };
}

function readSnapshots(getValue: (cookieName: string) => string): {
  first: UtmChannelSnapshot;
  last: UtmChannelSnapshot;
} {
  const first = emptySnapshot();
  const last = emptySnapshot();
  for (const key of UTM_CHANNEL_KEYS) {
    first[key] = getValue(`first_channel_${key}`);
    last[key] = getValue(`last_channel_${key}`);
  }
  return { first, last };
}

/**
 * Reads first-touch / last-touch channel cookies in the browser.
 * During SSR, `getUTMCookie` returns empty strings.
 */
export function getStoredUtmChannelAttribution(): {
  first: UtmChannelSnapshot;
  last: UtmChannelSnapshot;
} {
  return readSnapshots((name) => getUTMCookie(name));
}

/**
 * Server Components, Route Handlers, Server Actions — pass `cookies()` from `next/headers`
 * or `NextRequest#cookies`.
 */
export function getStoredUtmChannelAttributionFromRequestCookies(cookies: {
  get: (name: string) => { value: string } | undefined;
}): {
  first: UtmChannelSnapshot;
  last: UtmChannelSnapshot;
} {
  return readSnapshots((name) => cookies.get(name)?.value ?? "");
}

/** Last-touch values using standard `utm_*` names (for APIs / analytics). Omits empty entries. */
export function lastTouchUtmParamsNonEmpty(
  last: UtmChannelSnapshot
): Record<string, string> {
  const map: Record<string, string> = {
    utm_source: last.source,
    utm_medium: last.medium,
    utm_campaign: last.campaign,
    utm_content: last.content,
    utm_term: last.term,
    utm_id: last.id,
  };
  return Object.fromEntries(
    Object.entries(map).filter(([, v]) => v.trim().length > 0)
  );
}

/** Same as {@link lastTouchUtmParamsNonEmpty} but includes empty keys as empty strings. */
export function lastTouchUtmParams(last: UtmChannelSnapshot): Record<string, string> {
  return {
    utm_source: last.source,
    utm_medium: last.medium,
    utm_campaign: last.campaign,
    utm_content: last.content,
    utm_term: last.term,
    utm_id: last.id,
  };
}

/** Last-touch `fbclid` / `gclid` for lead API (omits empty values). */
export function lastTouchLeadClickIds(
  last: UtmChannelSnapshot
): Partial<Record<LeadClickIdKey, string>> {
  const out: Partial<Record<LeadClickIdKey, string>> = {};
  for (const key of LEAD_CLICK_ID_KEYS) {
    const v = last[key]?.trim();
    if (v) out[key] = v;
  }
  return out;
}

/** Reads `fbclid` / `gclid` from the current page URL (client-only). */
export function readLeadClickIdsFromSearch(
  search?: string
): Partial<Record<LeadClickIdKey, string>> {
  if (typeof window === "undefined" && search == null) return {};
  const params = new URLSearchParams(
    search ?? (typeof window !== "undefined" ? window.location.search : "")
  );
  const out: Partial<Record<LeadClickIdKey, string>> = {};
  for (const key of LEAD_CLICK_ID_KEYS) {
    const v = params.get(key)?.trim();
    if (v) out[key] = v;
  }
  return out;
}

/**
 * Last-touch cookies first, then current URL query params (for lead/create).
 */
export function resolveLeadClickIds(last?: UtmChannelSnapshot): Partial<
  Record<LeadClickIdKey, string>
> {
  const fromCookies = last ? lastTouchLeadClickIds(last) : {};
  const fromUrl = readLeadClickIdsFromSearch();
  const out: Partial<Record<LeadClickIdKey, string>> = { ...fromCookies };
  for (const key of LEAD_CLICK_ID_KEYS) {
    if (!out[key] && fromUrl[key]) out[key] = fromUrl[key];
  }
  return out;
}
